import './SetupWizard.scss';

import { FC, useContext, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AppContext } from 'components/AppContext/AppContext';
import { Constants } from 'constants/Constants';
import { useGuidedNavigationCollection } from 'customHooks/useGuidedNavigationCollection';
import { useLocalStorage } from 'customHooks/useLocalStorage';
import { useTokenExpiredHandler } from 'customHooks/useTokenExpiredHandler';
import { SearchType } from 'interfaces/DataSetOperators';
import Identifiable from 'interfaces/Identifiable';
import { AuthenticationStrategy } from 'models/AuthenticationStrategy';
import { CmsConnection } from 'models/CmsConnection';
import { CmsConnectionType } from 'models/CmsConnectionType';
import { Company } from 'models/Company';
import { ConnectionStatus } from 'models/ConnectionStatus';
import { Entities, EntityName } from 'models/Entities';
import { LogLevel } from 'models/LogLevel';
import { Project } from 'models/Project';
import { useNavigate, useSearchParams } from 'react-router-dom';
import StepWizard from 'react-step-wizard';

import ConfigureCms from '../ConfigureCms/ConfigureCms';
import ConfigureSocial from '../ConfigureSocial/ConfigureSocial';
import ImportCmsCategories from '../ImportCmsCategories/ImportCmsCategories';
import NewProject from '../NewProject/NewProject';
import SetupCompany from '../SetupCompany/SetupCompany';

import { SetupFormData } from './SetupFormData';
import { useEntitySetupHandler } from './useEntitySetupHandler';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface SetupWizardProps { }

interface WizardStepChanged {
  activeStep: number;
  previousStep: number;
}

// see: https://github.com/jcmcneal/react-step-wizard/blob/master/app/components/wizard.js
// https://www.npmjs.com/package/react-step-wizard

const SetupWizard: FC<SetupWizardProps> = () => {
  const { configRepo, setIsProjectSelectable, setSelectedProjectIdAsync, selectedProjectId, sendLog } =
    useContext(AppContext);
  const onTokenExpired = useTokenExpiredHandler();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNewProject = searchParams.get('new_project');
  const [hasEditorIntroPlayed] = useLocalStorage<boolean>(
    sendLog,
    Constants.EDIT_DOCUMENT_INTRO_HAS_PLAYED,
    false,
  );

  const defaultForm: SetupFormData = {
    Company: {
      name: '',
      ownerName: '',
    },
    Project: {
      name: '',
      companyId: 0,
      description: '',
      writerName: '',
      writerEmailAddress: '',
      pmName: '',
      pmEmailAddress: '',
      approver1Name: '',
      approver1Email: '',
      websiteUrl: '',
      authorName: '',
      articleFooterTemplate: '',
    },
    CmsConnection: {
      projectId: 0,
      connectionStatus: ConnectionStatus.OK,
      siteUrl: '',
      username: null,
      applicationPassword: null,
      authenticationStrategy: AuthenticationStrategy.ApplicationPassword,
      connectionType: CmsConnectionType.WordPress,
    },
    CmsCategories: [],
    PlanLevel: {},
    InvitationList: [],
  };
  const [state, updateState] = useState({ form: defaultForm });
  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    setIsProjectSelectable(false);
  }, []);

  const tour = useGuidedNavigationCollection();
  useEffect(() => {
    tour.startTourFromQueryParams();
  }, []);

  useEffect(() => {
    if (isNewProject === 'true') {
      const updateSelectedProjectId = async () => {
        await setSelectedProjectIdAsync(undefined);
      }
      updateSelectedProjectId().catch((e) => {
        sendLog(e.message, LogLevel.error, e);
      });
    }
  }, [isNewProject]);

  const updateFormEntity = (entityName: string, value: object) => {
    updateState((previousState) => {
      const updatedForm: SetupFormData = { ...previousState.form };
      (updatedForm as any)[entityName] = { ...value };
      return {
        form: updatedForm,
      };
    });
  };

  const updateForm = (entityName: string, key: string, value: any) => {
    updateState((previousState) => {
      const updatedForm: SetupFormData = { ...previousState.form };
      (updatedForm as any)[entityName][key] = value;
      return {
        form: updatedForm,
      };
    });
  };

  const onCompanyLoaded = async (company: Company) => {
    updateFormEntity(EntityName.Company, company);
  };
  const companyHandler = useEntitySetupHandler(
    configRepo!.SCHEDULE_API_BASE_URI,
    sendLog,
    onTokenExpired,
    onCompanyLoaded,
    Entities.Companies,
  );

  const onProjectLoaded = async (project: Project) => {
    updateFormEntity(EntityName.Project, project);
  };
  const projectHandler = useEntitySetupHandler(
    configRepo!.SCHEDULE_API_BASE_URI,
    sendLog,
    onTokenExpired,
    onProjectLoaded,
    Entities.Projects,
  );

  const onCmsConnectionLoaded = async (cmsConnection: CmsConnection) => {
    const connection = { ...cmsConnection };
    connection.siteUrl = state.form.Project.websiteUrl;
    updateFormEntity(EntityName.CmsConnection, connection);
  };
  const cmsConnectionHandler = useEntitySetupHandler(
    configRepo!.PUBLISH_API_BASE_URI,
    sendLog,
    onTokenExpired,
    onCmsConnectionLoaded,
    Entities.CmsConnections,
  );

  useEffect(() => {
    if (!isAuthenticated || isLoading) {
      return;
    }

    const fetchCompanyInfo = async () => {
      const updatedToken = await getAccessTokenSilently({ cacheMode: 'off' });
      companyHandler.setAccessToken(updatedToken);
      await companyHandler.getSingleEntityByQuery({});
    };

    fetchCompanyInfo().catch((e) => {
      sendLog(e.message, LogLevel.error, e);
    });
  }, [isLoading, isAuthenticated]);

  const onComplete = async (_: WizardStepChanged) => {
    if (!hasEditorIntroPlayed) {
      navigate('/documents/edit/new');
    } else {
      navigate('/documents');
    }
  };

  const onStepChange = async (stepChangedEvent: WizardStepChanged) => {
    window.scrollTo(0, 0);
    if (stepChangedEvent.activeStep < stepChangedEvent.previousStep) {
      return;
    }

    let updatedEntity: Identifiable | void;
    let updatedCmsConnection: CmsConnection;
    let updatedToken: string;

    switch (stepChangedEvent.previousStep) {
      case 1:
        updatedEntity = await companyHandler.createOrUpdateEntity(
          EntityName.Company,
          state.form.Company,
        );
        updateFormEntity(EntityName.Company, updatedEntity as object);
        updatedToken = await getAccessTokenSilently({ cacheMode: 'off' });
        projectHandler.setAccessToken(updatedToken);
        if (isNewProject !== 'true') {
          if (!selectedProjectId) {
            projectHandler.getSingleEntityByQuery({
              SearchProperty: 'CompanyId',
              SearchType: SearchType.EqualTo,
              SearchValue: updatedEntity!.id as string,
            });
          } else {
            projectHandler.getSingleEntityByQuery({
              SearchProperty: 'Id',
              SearchType: SearchType.EqualTo,
              SearchValue: selectedProjectId.toString(),
            });
          }
        }
        updateForm(EntityName.Project, 'companyId', updatedEntity!.id);
        break;
      case 2:
        updatedEntity = await projectHandler.createOrUpdateEntity(
          EntityName.Project,
          state.form.Project,
        );
        updateFormEntity(EntityName.Project, updatedEntity as object);
        updatedToken = await getAccessTokenSilently({ cacheMode: 'off' });

        cmsConnectionHandler.setAccessToken(updatedToken);

        // must do this AFTER await getAccessTokenSilently() because changing navigation history for some reason stops calling
        // auth0 for a new token and instead uses the cache
        if (updatedEntity?.id) {
          await setSelectedProjectIdAsync(updatedEntity!.id as number);
          if (isNewProject) {
            navigate('/account', { replace: true });
          }
        }

        cmsConnectionHandler.getSingleEntityByQuery({ ProjectId: updatedEntity!.id as number });
        updatedCmsConnection = { ...state.form.CmsConnection };
        updatedCmsConnection.projectId = updatedEntity!.id as number;
        updatedCmsConnection.siteUrl = (updatedEntity as Project).websiteUrl;
        updateFormEntity(EntityName.CmsConnection, updatedCmsConnection);
        break;
      case 3:
        // super hacky, but needed until a better solution is found for race condition in state:
        if (state.form.CmsConnection.siteUrl !== state.form.Project.websiteUrl) {
          updateForm(EntityName.CmsConnection, 'siteUrl', state.form.Project.websiteUrl);
          state.form.CmsConnection.siteUrl = state.form.Project.websiteUrl;
        }
        if (state.form.CmsConnection.connectionType === 'None') {
          if (!state.form.CmsConnection.username) {
            state.form.CmsConnection.username = 'none';
          }
          if (!state.form.CmsConnection.applicationPassword) {
            state.form.CmsConnection.applicationPassword = 'none';
          }
        }
        updatedEntity = await cmsConnectionHandler.createOrUpdateEntity(
          EntityName.CmsConnection,
          state.form.CmsConnection,
        );
        updateFormEntity(EntityName.CmsConnection, updatedEntity as object);
        break;
      case 4:
        break;
      case 5:
        break;
      default:
        throw new Error('Unexpected step number in Wizard');
    }
  };

  return (
    <div className="SetupWizard">
      <StepWizard onStepChange={onStepChange} initialStep={1}>
        <SetupCompany onUpdate={updateForm} cachedCompany={state.form.Company} step={1} />
        <NewProject onUpdate={updateForm} cachedProject={state.form.Project} step={2} />
        <ConfigureCms
          onUpdate={updateForm}
          cachedCmsConnection={state.form.CmsConnection}
          step={3}
        />
        <ImportCmsCategories cachedProjectId={state.form.Project.id} cachedCmsConnectionId={state.form.CmsConnection.id} step={4} />
        <ConfigureSocial step={5} onComplete={onComplete} />
      </StepWizard>
    </div>
  );
};

export default SetupWizard;
